import { palette } from '../components/styles/colors'
import { SEGPAY_UI_VERSION } from '../constants/endpoints'
import { UserAccess, isLevelPremiumOrHigher } from './userProperties/level'

export enum ProcessorName {
    CCBILL = 'CCBill',
    EPOCH = 'Epoch',
    NETBILLING = 'Netbilling',
    ROCKETGATE = 'RocketGate',
    SEGPAY = 'Segpay',
}

export enum ProcessorID {
    CCBILL = 4,
    NETBILLING = 9,
    ROCKETGATE = 23,
    SEGPAY = 24,
}

export const processorNameToID = (name: ProcessorName) =>
    ({
        [ProcessorName.CCBILL]: ProcessorID.CCBILL,
        [ProcessorName.NETBILLING]: ProcessorID.NETBILLING,
        [ProcessorName.ROCKETGATE]: ProcessorID.ROCKETGATE,
        [ProcessorName.SEGPAY]: ProcessorID.SEGPAY,
        [ProcessorName.EPOCH]: ProcessorID.SEGPAY,
    })[name]

export enum PackageID {
    TOKENS_200 = 600,
    TOKENS_550 = 601,
    TOKENS_900 = 602,
    TOKENS_1875 = 603,
    TOKENS_3775 = 604,
    TOKENS_7575 = 605,
}

export enum PackageProduct {
    TOKENS_10 = '635+Credit+Card',
    TOKENS_15 = '636+Credit+Card',
    TOKENS_20 = '637+Credit+Card',
    TOKENS_25 = '620+Credit+Card',
    TOKENS_30 = '621+Credit+Card',
    TOKENS_35 = '622+Credit+Card',
    TOKENS_40 = '623+Credit+Card',
    TOKENS_45 = '624+Credit+Card',
    TOKENS_50 = '625+Credit+Card',
    TOKENS_55 = '626+Credit+Card',
    TOKENS_60 = '627+Credit+Card',
    TOKENS_65 = '628+Credit+Card',
    TOKENS_70 = '629+Credit+Card',
    TOKENS_75 = '630+Credit+Card',
    TOKENS_80 = '631+Credit+Card',
    TOKENS_85 = '632+Credit+Card',
    TOKENS_90 = '633+Credit+Card',
    TOKENS_95 = '634+Credit+Card',
    TOKENS_200 = '600+Credit+Card',
    TOKENS_550 = '601+Credit+Card',
    TOKENS_900 = '602+Credit+Card',
    TOKENS_1875 = '603+Credit+Card',
    TOKENS_3775 = '604+Credit+Card',
    TOKENS_7575 = '605+Credit+Card',
}

export enum PackagePrice {
    TOKENS_200 = 19.99,
    TOKENS_550 = 49.99,
    TOKENS_900 = 74.99,
    TOKENS_1875 = 149.99,
    TOKENS_3775 = 299.99,
    TOKENS_7575 = 599.99,
}

export enum PackageVolume {
    TOKENS_200 = 200,
    TOKENS_550 = 550,
    TOKENS_900 = 900,
    TOKENS_1875 = 1875,
    TOKENS_3775 = 3775,
    TOKENS_7575 = 7575,
}

export interface Product {
    amount: PackagePrice
    available_processors: Array<ProcessorID>
    available: boolean
    description: string
    id: PackageID
    name: string
    volume_type: 'token'
    volume: PackageVolume
    purchase_urls?: Partial<Record<ProcessorID, string>>
}

export type Products = Record<PackageID, Product>

export interface PreviousPurchase {
    amount: PackagePrice
    days_since: number
    processor_id: ProcessorID
    product_id: PackageID
    transaction_date: string
}

export interface ProcessorNameID {
    id: ProcessorID
    name: ProcessorName
}

export interface ProcessorDetails {
    description: string
    display_name: string
    id: ProcessorID | number
    name: ProcessorName | string
    payment_types: string
}

const PAYMENT_TYPES_IMAGE_HOSTS = ['www.myfreecams.com', 'assets.mfcimg.com']
const PAYMENT_TYPES_IMAGE = /^\s*<img\b[^>]*\bsrc=["']([^"']+)["'][^>]*>\s*$/i

export type PaymentTypes =
    { kind: 'image'; src: string } | { kind: 'text'; text: string }

/**
 * `payment_types` is either a plain list ("Visa, MasterCard") or, for
 * Segpay's PayPal option, an `<img>` tag. Only an https image on an MFC host
 * is rendered as an image; anything else is shown as text with tags dropped,
 * or as `fallback` (the provider's name) when nothing is left.
 */
export const parsePaymentTypes = (
    paymentTypes: string | undefined,
    fallback = '',
): PaymentTypes => {
    const src = paymentTypes?.match(PAYMENT_TYPES_IMAGE)?.[1]
    if (src) {
        try {
            const url = new URL(src)
            if (
                url.protocol === 'https:' &&
                PAYMENT_TYPES_IMAGE_HOSTS.includes(url.hostname)
            ) {
                return { kind: 'image', src: url.href }
            }
        } catch {
            // not a url
        }
    }
    return {
        kind: 'text',
        text: (paymentTypes ?? '').replace(/<[^>]*>/g, '').trim() || fallback,
    }
}

/** processors keyed by id; sparse, the API only returns enabled ones */
export type ProcessorsMap = Partial<Record<ProcessorID, ProcessorDetails>>

export interface PurchaseLimits {
    brief?: unknown
    code?: unknown
    daily_token_limit_amount: number
    description?: string
    err?: unknown
    total_limit: number
}

export interface PurchaseInfoResponse {
    code?: number
    description?: number
    err?: number
    footer?: string
    available_quickbuy_processors: Array<ProcessorID | number>
    disable_in_chat_quickbuy: 0 | 1
    previous_purchase?: PreviousPurchase
    processor_priority: Array<ProcessorID>
    processors: ProcessorsMap
    products: Record<PackageID, Product>
    purchase_limits: PurchaseLimits
    segpay_ui_version?: number
    success: boolean
}

export interface TokenProduct {
    amount: PackagePrice | number
    description: string
    id: PackageID | number
    name: string
    processors: Array<ProcessorID | number>
    processorsMap?: ProcessorsMap
    purchaseUrls?: Partial<Record<ProcessorID, string>>
    volume_type: 'token'
    volume: PackageVolume | number
}

export interface ITokenPurchaseState {
    error?: string
    footer?: string
    /** the member's last successful purchase, when the API knows one */
    previousPurchase?: PreviousPurchase
    processorsByPriority?: Array<ProcessorDetails>
    processorsMap?: ProcessorsMap
    /** the available products, in API order */
    products?: Array<TokenProduct>
    quickbuy?: {
        processorsMap?: ProcessorsMap
        processors: Array<ProcessorID | string>
        disable_in_chat: 0 | 1
    }
    segpayUiVersion?: number
    /** only set when the member may buy the hardcoded small packages */
    smallProducts?: SmallPackages
}

/**
 * Segpay's v2 UI is not framed: as on www, that checkout opens in a popup on
 * the provider's own url, or on `fallback` (purchase.php) when the API gave
 * none. Undefined keeps the iframe flow.
 */
export const getPopupCheckoutUrl = (
    options: ITokenPurchaseState,
    processor: ProcessorName | string,
    product: PackageID | string,
    fallback: string,
): string | undefined => {
    if (
        processor !== (ProcessorName.SEGPAY as string) ||
        options.segpayUiVersion !== SEGPAY_UI_VERSION
    ) {
        return undefined
    }
    return (
        options.products?.find(({ id }) => String(id) === String(product))
            ?.purchaseUrls?.[ProcessorID.SEGPAY] ?? fallback
    )
}

export interface ProcessorExtrasState {
    background?: string
    height?: number | string
    width?: number | string
}

/**
 * Extra props that are app specific per payment provider
 */
export const processorExtras: Record<string, ProcessorExtrasState> = {
    [ProcessorName.NETBILLING]: {
        background: palette.drawerBackground,
        width: '100%',
        height: 36,
    },
    [ProcessorName.SEGPAY]: {
        background: palette.drawerBackground,
    },
}

/** premium or higher with CCBill enabled: only CCBill sells small packages (not in the API) */
export const hasSmallPurchaseAccess = (
    processorsMap: ProcessorsMap | undefined,
    level: UserAccess,
): boolean =>
    !!processorsMap?.[ProcessorID.CCBILL] && isLevelPremiumOrHigher(level)

/** a regular or small package row of the purchase drawer */
export interface TokenPackageOption {
    /** product id as the payment url wants it */
    id: string
    volume: number
    amount: number
    perToken: number
    processors: Array<ProcessorID | number>
    small: boolean
}

/** a product the drawer can price and sell; guards the per-token division */
const isSellable = ({ volume, amount }: { volume: number; amount: number }) =>
    volume > 0 && Number.isFinite(amount)

/**
 * Regular packages sorted by volume, each with its per-token price, followed
 * by the hardcoded small packages (CCBill only).
 */
export const buildTokenPackageOptions = (
    products: Array<TokenProduct> | undefined,
    smallProducts?: SmallPackages,
): Array<TokenPackageOption> => {
    const regular: Array<TokenPackageOption> = (products ?? [])
        .filter(isSellable)
        .map((product) => ({
            id: String(product.id),
            volume: product.volume,
            amount: product.amount,
            perToken: product.amount / product.volume,
            processors: product.processors,
            small: false,
        }))
        .sort((a, b) => a.volume - b.volume)

    const small: Array<TokenPackageOption> = (smallProducts ?? [])
        .filter(isSellable)
        .map((product) => ({
            id: product.state.product.split('+')[0] ?? '',
            volume: product.volume,
            amount: product.amount,
            perToken: product.amount / product.volume,
            processors: product.state.processor.map(processorNameToID),
            small: true,
        }))

    return [...regular, ...small]
}

/** the packages at least one of the member's enabled providers sells */
export const buildPurchasablePackageOptions = ({
    products,
    smallProducts,
    processorsByPriority = [],
}: ITokenPurchaseState): Array<TokenPackageOption> => {
    const enabled = processorsByPriority.map(({ id }) => id)
    return buildTokenPackageOptions(products, smallProducts).filter((option) =>
        option.processors.some((id) => enabled.includes(id)),
    )
}

/**
 * SmallPackages are not returned by the API as of 2/17/21
 */
export type SmallPackages = Array<{
    volume: number
    /** price in USD */
    amount: number
    state: {
        processor: Array<ProcessorName>
        product: PackageProduct
    }
}>

/**
 * Hardcoded Small Packages; as of 2/17/22 only CCBill is available for these
 * Small Products & metadata are not returned by the API
 */
export const smallPackages: SmallPackages = [
    {
        state: {
            product: PackageProduct.TOKENS_10,
            processor: [ProcessorName.CCBILL],
        },
        volume: 10,
        amount: 1.49,
    },
    {
        state: {
            product: PackageProduct.TOKENS_15,
            processor: [ProcessorName.CCBILL],
        },
        volume: 15,
        amount: 1.99,
    },
    {
        state: {
            product: PackageProduct.TOKENS_20,
            processor: [ProcessorName.CCBILL],
        },
        volume: 20,
        amount: 2.49,
    },
    {
        state: {
            product: PackageProduct.TOKENS_25,
            processor: [ProcessorName.CCBILL],
        },
        volume: 25,
        amount: 2.99,
    },
    {
        state: {
            product: PackageProduct.TOKENS_30,
            processor: [ProcessorName.CCBILL],
        },
        volume: 30,
        amount: 3.49,
    },
    {
        state: {
            product: PackageProduct.TOKENS_35,
            processor: [ProcessorName.CCBILL],
        },
        volume: 35,
        amount: 3.99,
    },
    {
        state: {
            product: PackageProduct.TOKENS_40,
            processor: [ProcessorName.CCBILL],
        },
        volume: 40,
        amount: 4.49,
    },
    {
        state: {
            product: PackageProduct.TOKENS_45,
            processor: [ProcessorName.CCBILL],
        },
        volume: 45,
        amount: 4.99,
    },
    {
        state: {
            product: PackageProduct.TOKENS_50,
            processor: [ProcessorName.CCBILL],
        },
        volume: 50,
        amount: 5.49,
    },
    {
        state: {
            product: PackageProduct.TOKENS_55,
            processor: [ProcessorName.CCBILL],
        },
        volume: 55,
        amount: 5.99,
    },
    {
        state: {
            product: PackageProduct.TOKENS_60,
            processor: [ProcessorName.CCBILL],
        },
        volume: 60,
        amount: 6.49,
    },
    {
        state: {
            product: PackageProduct.TOKENS_65,
            processor: [ProcessorName.CCBILL],
        },
        volume: 65,
        amount: 6.99,
    },
    {
        state: {
            product: PackageProduct.TOKENS_70,
            processor: [ProcessorName.CCBILL],
        },
        volume: 70,
        amount: 7.49,
    },
    {
        state: {
            product: PackageProduct.TOKENS_75,
            processor: [ProcessorName.CCBILL],
        },
        volume: 75,
        amount: 7.99,
    },
    {
        state: {
            product: PackageProduct.TOKENS_80,
            processor: [ProcessorName.CCBILL],
        },
        volume: 80,
        amount: 8.49,
    },
    {
        state: {
            product: PackageProduct.TOKENS_85,
            processor: [ProcessorName.CCBILL],
        },
        volume: 85,
        amount: 8.99,
    },
    {
        state: {
            product: PackageProduct.TOKENS_90,
            processor: [ProcessorName.CCBILL],
        },
        volume: 90,
        amount: 9.49,
    },
    {
        state: {
            product: PackageProduct.TOKENS_95,
            processor: [ProcessorName.CCBILL],
        },
        volume: 95,
        amount: 9.99,
    },
]
